import React from 'react';
import type { ReactNode } from 'react';
import { portfolioApi } from "../services/indexSharedServices";

interface User {
  id: string;
  email: string;
  isAdmin: boolean;
  role?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
  checkAuth: () => Promise<void>;
  isAdmin: boolean;
}

//  EXPORT du AuthContext
export const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [authState, setAuthState] = React.useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null
  });

  // ================================
  // FONCTIONS D'AUTHENTIFICATION
  // ================================

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      
      const response = await portfolioApi.auth.login({ email, password });
      
      if (response.success && response.data) {
        const { user: userData, token } = response.data;
        
        const user: User = {
          id: userData.id,
          email: userData.email,
          isAdmin: userData.isAdmin ?? userData.is_admin ?? false,
          role: userData.role
        };
        
        setAuthState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });
        
        // Sauvegarder le token
        localStorage.setItem('auth_token', token);
        
        return true;
      } else {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: response.error || 'Échec de la connexion'
        }));
        return false;
      }
    } catch (error: any) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Erreur de connexion'
      }));
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await portfolioApi.auth.logout();
    } catch (error) {
      console.log('Logout error:', error);
    } finally {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });
      localStorage.removeItem('auth_token');
    }
  };

  const clearError = (): void => {
    setAuthState(prev => ({ ...prev, error: null }));
  };

  const checkAuth = async (): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        });
        return;
      }

      
      const response = await portfolioApi.auth.verifyToken();
      
      if (response.success && response.data?.valid) {
        const userResponse = await portfolioApi.auth.getCurrentUser();

if (userResponse.success && userResponse.data) {
  const userData = userResponse.data;
  const user: User = {
    id: userData.id,
    email: userData.email,
    isAdmin: userData.isAdmin ?? userData.is_admin ?? false,
    role: userData.role
  };
          
          setAuthState({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null
          });
          
        } else {
          localStorage.removeItem('auth_token');
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: 'Données utilisateur non disponibles'
          });
        }
      } else {
        localStorage.removeItem('auth_token');
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        });
      }
    } catch (error: any) {
      localStorage.removeItem('auth_token');
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });
    }
  };

  // ================================
  // INITIALISATION
  // ================================

  React.useEffect(() => {
    checkAuth();
  }, []);

  // ================================
  // CONTEXTE VALUE
  // ================================

  const contextValue: AuthContextType = {
    user: authState.user,
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    error: authState.error,
    login,
    logout,
    clearError,
    checkAuth,
    isAdmin: authState.user?.isAdmin || false
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

//  EXPORT du type aussi
export type { AuthContextType };

export default AuthProvider;